import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Header from "./components/Header/Header";
import Hero from "./components/Hero/Hero";
import Promo from "./components/Promo/Promo";
import Services from "./components/Services/Services";
import Process from "./components/Process/Process";
import Reviews from "./components/Reviews/Reviews";
import Pricing from "./components/Pricing/Pricing";
import Contact from "./components/Contact/Contact";
import Footer from "./components/Footer/Footer";
import PDFModal from "./components/PDFModal/PDFModal";
import AppInstallModal from "./components/AppInstallModal/AppInstallModal";
import SocialAuthProvider from "./hooks/SocialAuthProvider/SocialAuthProvider";
import MembershipProvider from "./hooks/MembershipProvider/MembershipProvider";
import { CONFIG } from "./utils/config";
import { useDocumentHead } from "./hooks/useDocumentHead";
import { appleLogin } from "./utils/auth/appleAuth";
import "./styles/global.css";
import { routers } from "./utils/routers";
import { ApiProvider, useApi } from "./hooks/ApiProvider";
import { useMembership } from "./hooks/MembershipProvider/MembershipProvider";

function App() {
  const [activeSection, setActiveSection] = useState<string>("home");
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
  const [isAppInstallModalOpen, setIsAppInstallModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState<string>("home");

  // Apple OAuth callback 처리
  useEffect(() => {
    // localStorage에서 Apple OAuth 코드 확인
    const appleCode = localStorage.getItem("apple_auth_code");
    const appleState = localStorage.getItem("apple_auth_state");
    const appleUser = localStorage.getItem("apple_auth_user");

    if (appleCode) {
      // localStorage에서 코드 제거
      localStorage.removeItem("apple_auth_code");
      localStorage.removeItem("apple_auth_state");
      localStorage.removeItem("apple_auth_user");

      // Apple OAuth 처리
      const handleAppleAuth = async () => {
        try {
          // appleLogin 함수 호출 (navigate 함수는 나중에 전달)
          const navigate = (path: string, options?: any) => {
            window.location.href = path;
          };

          await appleLogin(navigate);
        } catch (error) {
          console.error("Apple OAuth 처리 실패:", error);
          // 에러 처리 (예: 로그인 페이지로 리다이렉트)
          window.location.href = "/login";
        }
      };

      handleAppleAuth();
    }
  }, []);

  // SEO 메타데이터 설정
  useDocumentHead({
    title: `${CONFIG.SEO.SITE_NAME} - ${CONFIG.SEO.SITE_DESCRIPTION}`,
    description: CONFIG.SEO.SITE_DESCRIPTION,
    keywords: CONFIG.SEO.SITE_KEYWORDS,
    author: CONFIG.SEO.AUTHOR,
    canonical: CONFIG.SEO.CANONICAL_URL,
    robots: "index, follow",
    // Open Graph
    ogTitle: `${CONFIG.SEO.SITE_NAME} - ${CONFIG.SEO.SITE_DESCRIPTION}`,
    ogDescription: CONFIG.SEO.SITE_DESCRIPTION,
    ogType: "website",
    ogUrl: CONFIG.SEO.SITE_URL,
    ogImage: CONFIG.IMAGES.LOGO_FULL_URL,
    ogSiteName: CONFIG.SEO.SITE_NAME,
    ogLocale: "ko_KR",
    // Twitter Card
    twitterCard: "summary_large_image",
    twitterTitle: `${CONFIG.SEO.SITE_NAME} - ${CONFIG.SEO.SITE_DESCRIPTION}`,
    twitterDescription: CONFIG.SEO.SITE_DESCRIPTION,
    twitterImage: CONFIG.IMAGES.LOGO_FULL_URL,
    // 추가 메타 태그
    customMeta: [{ name: "language", content: CONFIG.SEO.LANGUAGE }],
  });

  // URL 해시 처리 함수
  const scrollToHashSection = (hash: string) => {
    const sectionId = hash.replace("#", "");
    const element = document.getElementById(sectionId);

    if (element) {
      // 헤더 높이만큼 오프셋 조정
      const headerHeight = 80;
      const elementPosition = element.offsetTop - headerHeight;

      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });

      setActiveSection(sectionId);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      // MyPage에서는 커스텀 이벤트로 처리되므로 여기서는 홈페이지 섹션만 처리
      if (currentPage === "home") {
        const sections = [
          "home",
          "promo",
          "services",
          "process-section",
          "reviews",
          "concerns",
          "pricing",
          "contact",
        ];

        const scrollPosition = window.scrollY + 100;

        for (const sectionId of sections) {
          const element = document.getElementById(sectionId);
          if (element) {
            const offsetTop = element.offsetTop;
            const offsetHeight = element.offsetHeight;

            if (
              scrollPosition >= offsetTop &&
              scrollPosition < offsetTop + offsetHeight
            ) {
              setActiveSection(sectionId);
              break;
            }
          }
        }
      }
    };

    // MyPage에서 발생하는 커스텀 이벤트 처리
    const handleSectionChange = (event: CustomEvent) => {
      setActiveSection(event.detail.sectionId);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener(
      "sectionChange",
      handleSectionChange as EventListener
    );

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener(
        "sectionChange",
        handleSectionChange as EventListener
      );
    };
  }, [currentPage]);

  // 페이지 이동 시 자동 스크롤 방지
  useEffect(() => {
    // React Router의 기본 스크롤 복원 동작을 방지
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  const handleSamplePDFClick = () => {
    setIsPDFModalOpen(true);
  };

  const handleAppInstallClick = () => {
    setIsAppInstallModalOpen(true);
  };

  // 홈페이지 컴포넌트
  const HomePage = () => (
    <>
      <Hero onAppInstallClick={handleAppInstallClick} />
      <Promo />
      <Services />
      <Process onSamplePDFClick={handleSamplePDFClick} />
      <Reviews />
      {/* <FAQ /> */}
      <Pricing />
      <Contact />
    </>
  );

  // AppContent 컴포넌트 - MembershipProvider 내부에서 useMembership 사용

  return (
    <ApiProvider>
      <SocialAuthProvider>
        <MembershipProvider>
          <AppContent
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            isPDFModalOpen={isPDFModalOpen}
            setIsPDFModalOpen={setIsPDFModalOpen}
            isAppInstallModalOpen={isAppInstallModalOpen}
            setIsAppInstallModalOpen={setIsAppInstallModalOpen}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            scrollToHashSection={scrollToHashSection}
            handleSamplePDFClick={handleSamplePDFClick}
            handleAppInstallClick={handleAppInstallClick}
            HomePage={HomePage}
          />
        </MembershipProvider>
      </SocialAuthProvider>
    </ApiProvider>
  );
}

const AppContent = ({
  activeSection,
  setActiveSection,
  isPDFModalOpen,
  setIsPDFModalOpen,
  isAppInstallModalOpen,
  setIsAppInstallModalOpen,
  currentPage,
  setCurrentPage,
  scrollToHashSection,
  handleSamplePDFClick,
  handleAppInstallClick,
  HomePage,
}: {
  activeSection: string;
  setActiveSection: (section: string) => void;
  isPDFModalOpen: boolean;
  setIsPDFModalOpen: (open: boolean) => void;
  isAppInstallModalOpen: boolean;
  setIsAppInstallModalOpen: (open: boolean) => void;
  currentPage: string;
  setCurrentPage: (page: string) => void;
  scrollToHashSection: (hash: string) => void;
  handleSamplePDFClick: () => void;
  handleAppInstallClick: () => void;
  HomePage: () => React.ReactElement;
}) => {
  const { isEmbed } = useMembership();

  // 페이지 변경 감지 및 스크롤 처리
  const PageHandler = () => {
    const location = useLocation();
    const { getMyHome } = useApi();
    const { login } = useMembership();

    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const accessTokenFromQuery = params.get("token");
      const localToken = localStorage.getItem("membership_access_token");

      if (accessTokenFromQuery) {
        if (localToken !== accessTokenFromQuery) {
          localStorage.setItem("membership_access_token", accessTokenFromQuery);
          getMyHome()
            .then((res: any) => {
              const user_profile = res?.user_profile;
              if (user_profile) {
                localStorage.setItem(
                  "membership_user_profile",
                  JSON.stringify(user_profile)
                );
                login(user_profile, accessTokenFromQuery, undefined, true);
              }
            })
            .catch((err: any) => {
              if (localToken) {
                localStorage.setItem("membership_access_token", localToken);
              }
            });
        } else {
          const user_profile = JSON.parse(
            localStorage.getItem("membership_user_profile") || "{}"
          );
          if (user_profile) {
            login(user_profile, accessTokenFromQuery, undefined, true);
          }
        }
      }
    }, []);

    useEffect(() => {
      // URL에 따라 현재 페이지 설정
      if (location.pathname === "/mypage") {
        setCurrentPage("mypage");
      } else if (location.pathname === "/login") {
        setCurrentPage("login");
      } else if (location.pathname === "/auth/apple/callback") {
        setCurrentPage("apple");
      } else if (location.pathname === "/order") {
        setCurrentPage("order");
      } else if (location.pathname === "/phone-verification") {
        setCurrentPage("phone-verification");
      } else if (location.pathname === "/delivery") {
        setCurrentPage("delivery");
      } else if (location.pathname === "/order/fail") {
        setCurrentPage("fail");
      } else if (location.pathname.startsWith("/shared/")) {
        setCurrentPage("shared");
      } else if (location.pathname.startsWith("/link/")) {
        setCurrentPage("links");
      } else if (location.pathname.startsWith("/app-install")) {
        setCurrentPage("app-install");
      } else {
        setCurrentPage("home");
        if (location.hash) {
          setTimeout(() => {
            scrollToHashSection(location.hash);
          }, 100);
        }
      }
    }, [location.pathname, location.hash]);

    return null;
  };

  return (
    <Router>
      <div className="App">
        {currentPage !== "delivery" &&
          currentPage !== "fail" &&
          currentPage !== "phone-verification" &&
          currentPage !== "apple" &&
          currentPage !== "shared" &&
          currentPage !== "links" &&
          currentPage !== "app-install" &&
          !isEmbed() && <Header activeSection={activeSection} />}
        <PageHandler />

        <Routes>
          {routers?.map((route, idx) => {
            return (
              <Route key={idx} path={route?.path} element={route?.element} />
            );
          })}
        </Routes>

        {/* 모달들 */}
        <PDFModal
          isOpen={isPDFModalOpen}
          onClose={() => setIsPDFModalOpen(false)}
        />
        <AppInstallModal
          isOpen={isAppInstallModalOpen}
          onClose={() => setIsAppInstallModalOpen(false)}
        />
        {currentPage !== "delivery" &&
          currentPage !== "fail" &&
          currentPage !== "phone-verification" &&
          currentPage !== "apple" &&
          currentPage !== "shared" &&
          currentPage !== "app-install" &&
          currentPage !== "links" && <Footer />}
      </div>
    </Router>
  );
};

export default App;
